import type { ReactNode, JSX } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Center, Loader, Title, Stack } from '@mantine/core';
import { useAuth } from './auth-context';
import type { AuthRole } from './types';

function ForbiddenPage(): JSX.Element {
  return (
    <Center h="100vh">
      <Stack align="center">
        <Title order={2}>Access Denied</Title>
        <p>You do not have permission to view this page.</p>
      </Stack>
    </Center>
  );
}

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Center h="100vh">
        <Loader aria-label="Loading..." />
      </Center>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  return <>{children}</>;
}

interface RequireRoleProps {
  children: ReactNode;
  allow: AuthRole[];
}

export function RequireRole({ children, allow }: RequireRoleProps): JSX.Element {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Center h="100vh">
        <Loader aria-label="Loading..." />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allow.includes(user.role)) {
    return <ForbiddenPage />;
  }

  return <>{children}</>;
}
